import type { SerializedNode, ConversionContext } from '../types';
import {
  ANIMATION_TYPES,
  ANIMATION_EASINGS,
  HOVER_EFFECT_TYPES,
  type AnimationType,
  type AnimationEasing,
  type HoverEffectType,
} from '../../types/animation';
import { getAttribute } from '../parser';
import {
  parseBoolean,
  parseInteger,
  parseFloat_,
  parsePadding,
  parseBorderRadius,
  parsePosition,
  parsePositionOffset,
  inferPositionAnchorsState,
  parseShadowAttributes,
  SHADOW_ATTRIBUTES,
} from '../utils';
import { parseColor } from '../utils/color-parser';
import { BUTTON_DEFAULTS, getLeafFillSpace } from '../utils/defaults';
import { BUTTON_NODE_NAME, EXTERNAL_LINK_EVENT, NO_LINK_EVENT } from '../../utils/constants';

/**
 * Transform a <button> XML element into a CraftJS Button node
 *
 * By default, buttons use global styles (attached="true"). When attached="false",
 * all styling properties become configurable via XML attributes.
 * When inline style attributes (bg, color, borderRadius, padding, margin) are present,
 *
 * @param element - The XML element
 * @param context - Conversion context
 * @returns SerializedNode for the Button
 */
export const transformButton = (element: Element, context: ConversionContext): SerializedNode => {
  // Parse text from inline content
  const textContent = element.textContent?.trim();
  const text = textContent || BUTTON_DEFAULTS.text;

  // Parse variant (primary, secondary, outline)
  const variantAttr = getAttribute(element, 'variant');
  const buttonVariant = mapButtonVariant(variantAttr) || BUTTON_DEFAULTS.buttonVariant;

  // Parse size (small, medium, large)
  const sizeAttr = getAttribute(element, 'size');
  const buttonSize = mapButtonSize(sizeAttr) || BUTTON_DEFAULTS.buttonSize;

  // Parse click behavior
  const clickUrlAttr = getAttribute(element, 'clickUrl');
  const openInNewTabAttr = getAttribute(element, 'openInNewTab');
  const openInNewTab = parseBoolean(openInNewTabAttr, false);
  const clickEvent = buildClickEvent(clickUrlAttr, openInNewTab);

  // Parse fullWidth
  const fullWidthAttr = getAttribute(element, 'fullWidth');
  const fillWidth = parseBoolean(fullWidthAttr, BUTTON_DEFAULTS.fillWidth);

  // Parse attached attribute - maps to appliedGlobalStyle
  const attachedAttr = getAttribute(element, 'attached');

  // Parse styling attributes (used when attached="false" or inline styles present)
  const fontSizeAttr = getAttribute(element, 'fontSize');
  const fontSize = fontSizeAttr || BUTTON_DEFAULTS.fontSize;

  const fontWeightAttr = getAttribute(element, 'fontWeight');
  const fontWeight = fontWeightAttr || BUTTON_DEFAULTS.fontWeight;

  const fontFamilyAttr = getAttribute(element, 'fontFamily');
  const fontFamily = fontFamilyAttr || BUTTON_DEFAULTS.fontFamily;

  const fontStyleAttr = getAttribute(element, 'fontStyle');
  const fontStyle = fontStyleAttr || BUTTON_DEFAULTS.fontStyle;

  const colorAttr = getAttribute(element, 'color');
  const color = colorAttr ? parseColor(colorAttr) : BUTTON_DEFAULTS.color;

  const bgAttr = getAttribute(element, 'bg');
  const backgroundColor = bgAttr ? parseColor(bgAttr) : BUTTON_DEFAULTS.backgroundColor;

  const paddingAttr = getAttribute(element, 'padding');
  const padding = parsePadding(paddingAttr, BUTTON_DEFAULTS.padding);

  const marginAttr = getAttribute(element, 'margin');
  const margin = parsePadding(marginAttr, BUTTON_DEFAULTS.margin);

  const borderRadiusAttr = getAttribute(element, 'borderRadius');
  const borderRadius = parseBorderRadius(borderRadiusAttr, BUTTON_DEFAULTS.borderRadius);

  const borderWidthAttr = getAttribute(element, 'borderWidth');
  const borderWidth = parseInteger(borderWidthAttr, BUTTON_DEFAULTS.borderWidth);

  const borderColorAttr = getAttribute(element, 'borderColor');
  const borderColor = borderColorAttr ? parseColor(borderColorAttr) : undefined;

  const hoverBgAttr = getAttribute(element, 'hoverBg');
  const hoverBackgroundColor = hoverBgAttr ? parseColor(hoverBgAttr) : undefined;

  const hoverBorderColorAttr = getAttribute(element, 'hoverBorderColor');
  const hoverBorderColor = hoverBorderColorAttr ? parseColor(hoverBorderColorAttr) : undefined;

  const hoverTextColorAttr = getAttribute(element, 'hoverTextColor');
  const hoverTextColor = hoverTextColorAttr ? parseColor(hoverTextColorAttr) : undefined;

  const letterSpacingAttr = getAttribute(element, 'letterSpacing');
  const letterSpacing = parseFloat_(letterSpacingAttr, BUTTON_DEFAULTS.letterSpacing);

  const textAlignAttr = getAttribute(element, 'textAlign');
  const textAlign = textAlignAttr || BUTTON_DEFAULTS.textAlign;

  // appliedGlobalStyle is only false when attached="false" is explicitly set
  const appliedGlobalStyle = parseBoolean(attachedAttr, true);

  // Parse position attributes
  const positionAttr = getAttribute(element, 'position');
  const position = parsePosition(positionAttr) || BUTTON_DEFAULTS.position;

  const topAttr = getAttribute(element, 'top');
  const top = parsePositionOffset(topAttr);

  const leftAttr = getAttribute(element, 'left');
  const left = parsePositionOffset(leftAttr);

  const rightAttr = getAttribute(element, 'right');
  const right = parsePositionOffset(rightAttr);

  const bottomAttr = getAttribute(element, 'bottom');
  const bottom = parsePositionOffset(bottomAttr);

  const zIndexAttr = getAttribute(element, 'zIndex');
  const zIndex = parseInteger(zIndexAttr, BUTTON_DEFAULTS.zIndex);

  // Infer position anchors state from provided values
  const positionAnchorsState = inferPositionAnchorsState(position, top, left, right, bottom);

  // Parse shadow attributes
  const shadow = parseShadowAttributes(element);

  // Parse tablet breakpoint overrides
  const tablet = parseBreakpointOverrides(element, 'tablet');

  // Parse mobile breakpoint overrides
  const mobile = parseBreakpointOverrides(element, 'mobile');

  // Build props object
  const props: Record<string, unknown> = {
    text,
    buttonVariant,
    buttonSize,
    clickEvent,
    fillWidth,
    appliedGlobalStyle,
    fontSize,
    fontWeight,
    fontFamily,
    fontStyle,
    color,
    backgroundColor,
    padding,
    margin,
    borderRadius,
    borderWidth,
    opacity: BUTTON_DEFAULTS.opacity,
    textAlign,
    letterSpacing,
    buttonType: BUTTON_DEFAULTS.buttonType,
    position,
    top: top || BUTTON_DEFAULTS.top,
    left: left || BUTTON_DEFAULTS.left,
    right: right || BUTTON_DEFAULTS.right,
    bottom: bottom || BUTTON_DEFAULTS.bottom,
    positionAnchorsState,
    zIndex,
    minWidth: BUTTON_DEFAULTS.minWidth,
    minHeight: BUTTON_DEFAULTS.minHeight,
    width: BUTTON_DEFAULTS.width,
    height: BUTTON_DEFAULTS.height,
    flexBasis: BUTTON_DEFAULTS.flexBasis,
    fillSpace: fillWidth
      ? getLeafFillSpace(context.parentFlexDirection, BUTTON_DEFAULTS.fillSpace)
      : BUTTON_DEFAULTS.fillSpace,
    visibility: BUTTON_DEFAULTS.visibility,
  };

  // Add optional styling props
  if (borderColor) props.borderColor = borderColor;
  if (hoverBackgroundColor) props.hoverBackgroundColor = hoverBackgroundColor;
  if (hoverBorderColor) props.hoverBorderColor = hoverBorderColor;
  if (hoverTextColor) props.hoverTextColor = hoverTextColor;
  if (shadow) {
    props.shadowType = shadow.shadowType;
    props.shadowSettings = shadow.shadowSettings;
  }

  // Parse animation attributes
  const animationProps = parseButtonAnimationAttributes(element);
  Object.assign(props, animationProps);

  // Add breakpoint overrides if present
  if (Object.keys(tablet).length > 0) props.tablet = tablet;
  if (Object.keys(mobile).length > 0) props.mobile = mobile;

  return {
    type: { resolvedName: BUTTON_NODE_NAME },
    isCanvas: false,
    props,
    displayName: BUTTON_NODE_NAME,
    custom: {
      displayName: BUTTON_NODE_NAME,
      isLocked: false,
    },
    hidden: false,
    nodes: [],
    linkedNodes: {},
    parent: context.parentId,
  };
};

/**
 * Parse breakpoint-specific overrides (tablet-* or mobile-* prefixed attributes)
 */
const parseBreakpointOverrides = (
  element: Element,
  breakpoint: 'tablet' | 'mobile',
): Record<string, unknown> => {
  const overrides: Record<string, unknown> = {};

  const paddingAttr = getAttribute(element, `${breakpoint}-padding`);
  if (paddingAttr) overrides.padding = parsePadding(paddingAttr, BUTTON_DEFAULTS.padding);

  const marginAttr = getAttribute(element, `${breakpoint}-margin`);
  if (marginAttr) overrides.margin = parsePadding(marginAttr, BUTTON_DEFAULTS.margin);

  const borderRadiusAttr = getAttribute(element, `${breakpoint}-borderRadius`);
  if (borderRadiusAttr)
    overrides.borderRadius = parseBorderRadius(borderRadiusAttr, BUTTON_DEFAULTS.borderRadius);

  // Position attributes
  const positionAttr = getAttribute(element, `${breakpoint}-position`);
  if (positionAttr) {
    const parsedPosition = parsePosition(positionAttr);
    if (parsedPosition) overrides.position = parsedPosition;
  }

  const topAttr = getAttribute(element, `${breakpoint}-top`);
  const leftAttr = getAttribute(element, `${breakpoint}-left`);
  const rightAttr = getAttribute(element, `${breakpoint}-right`);
  const bottomAttr = getAttribute(element, `${breakpoint}-bottom`);

  const top = parsePositionOffset(topAttr);
  const left = parsePositionOffset(leftAttr);
  const right = parsePositionOffset(rightAttr);
  const bottom = parsePositionOffset(bottomAttr);

  if (top !== undefined) overrides.top = top;
  if (left !== undefined) overrides.left = left;
  if (right !== undefined) overrides.right = right;
  if (bottom !== undefined) overrides.bottom = bottom;

  // Infer positionAnchorsState if any position props are provided
  const hasPositionOverride = positionAttr || topAttr || leftAttr || rightAttr || bottomAttr;
  if (hasPositionOverride) {
    const breakpointPosition = parsePosition(positionAttr);
    overrides.positionAnchorsState = inferPositionAnchorsState(
      breakpointPosition,
      top,
      left,
      right,
      bottom,
    );
  }

  const zIndexAttr = getAttribute(element, `${breakpoint}-zIndex`);
  if (zIndexAttr) overrides.zIndex = parseInteger(zIndexAttr, 0);

  return overrides;
};

/**
 * Map variant attribute to CraftJS buttonVariant value
 */
const mapButtonVariant = (value: string | null): 'primary' | 'secondary' | 'accent' | undefined => {
  if (!value) return undefined;

  const normalized = value.toLowerCase().trim();
  switch (normalized) {
    case 'primary':
      return 'primary';
    case 'secondary':
      return 'secondary';
    case 'accent':
      return 'accent';
    default:
      return undefined;
  }
};

/**
 * Map size attribute to CraftJS buttonSize value
 */
const mapButtonSize = (value: string | null): 'small' | 'medium' | 'large' | undefined => {
  if (!value) return undefined;

  const normalized = value.toLowerCase().trim();
  switch (normalized) {
    case 'small':
      return 'small';
    case 'medium':
      return 'medium';
    case 'large':
      return 'large';
    default:
      return undefined;
  }
};

/**
 * Build a click event object for the button
 */
const buildClickEvent = (url: string | null, openInNewTab: boolean) => {
  if (!url) {
    return {
      eventType: NO_LINK_EVENT,
      eventData: null,
    };
  }

  return {
    eventType: EXTERNAL_LINK_EVENT,
    eventData: {
      url,
      openInNewTab,
    },
  };
};

/**
 * Parse animation and hover effect attributes from a button element
 */
const parseButtonAnimationAttributes = (element: Element): Record<string, unknown> => {
  const props: Record<string, unknown> = {};

  const animationTypeAttr = getAttribute(element, 'animationType');
  if (animationTypeAttr && (ANIMATION_TYPES as readonly string[]).includes(animationTypeAttr)) {
    props.animationType = animationTypeAttr as AnimationType;
  }

  const animationDurationAttr = getAttribute(element, 'animationDuration');
  if (animationDurationAttr) {
    props.animationDuration = parseInteger(animationDurationAttr, 600);
  }

  const animationDelayAttr = getAttribute(element, 'animationDelay');
  if (animationDelayAttr) {
    props.animationDelay = parseInteger(animationDelayAttr, 0);
  }

  const animationEasingAttr = getAttribute(element, 'animationEasing');
  if (
    animationEasingAttr &&
    (ANIMATION_EASINGS as readonly string[]).includes(animationEasingAttr)
  ) {
    props.animationEasing = animationEasingAttr as AnimationEasing;
  }

  const hoverEffectAttr = getAttribute(element, 'hoverEffect');
  if (hoverEffectAttr && (HOVER_EFFECT_TYPES as readonly string[]).includes(hoverEffectAttr)) {
    props.hoverEffect = hoverEffectAttr as HoverEffectType;
  }

  const hoverTransitionDurationAttr = getAttribute(element, 'hoverTransitionDuration');
  if (hoverTransitionDurationAttr) {
    props.hoverTransitionDuration = parseInteger(hoverTransitionDurationAttr, 300);
  }

  const hoverIntensityAttr = getAttribute(element, 'hoverIntensity');
  if (hoverIntensityAttr) {
    props.hoverIntensity = parseInteger(hoverIntensityAttr, 105);
  }

  return props;
};

/**
 * Valid attributes for button elements
 * When attached="false" or inline style overrides are present, all styling attributes become configurable
 */
export const BUTTON_ATTRIBUTES = [
  // Core attributes
  'variant',
  'size',
  'fullWidth',
  'clickUrl',
  'openInNewTab',
  // Global style toggle
  'attached',
  // Typography (when detached)
  'fontSize',
  'fontWeight',
  'fontFamily',
  'fontStyle',
  'letterSpacing',
  'textAlign',
  // Colors (inline style overrides or when detached)
  'color',
  'bg',
  // Layout/borders (inline style overrides or when detached)
  'padding',
  'margin',
  'borderRadius',
  'borderWidth',
  'borderColor',
  // Hover states (when detached)
  'hoverBg',
  'hoverBorderColor',
  'hoverTextColor',
  // Shadow attributes
  ...SHADOW_ATTRIBUTES,
  // Position attributes
  'position',
  'top',
  'left',
  'right',
  'bottom',
  'zIndex',
  // Animation attributes
  'animationType',
  'animationDuration',
  'animationDelay',
  'animationEasing',
  'hoverEffect',
  'hoverTransitionDuration',
  'hoverIntensity',
  // Tablet breakpoint overrides
  'tablet-padding',
  'tablet-margin',
  'tablet-borderRadius',
  'tablet-position',
  'tablet-top',
  'tablet-left',
  'tablet-right',
  'tablet-bottom',
  'tablet-zIndex',
  // Mobile breakpoint overrides
  'mobile-padding',
  'mobile-margin',
  'mobile-borderRadius',
  'mobile-position',
  'mobile-top',
  'mobile-left',
  'mobile-right',
  'mobile-bottom',
  'mobile-zIndex',
] as const;

/**
 * Check if an attribute is valid for button elements
 */
export const isValidButtonAttribute = (attr: string): boolean => {
  return (BUTTON_ATTRIBUTES as readonly string[]).includes(attr);
};
